import React, { useState, useEffect, useContext } from "react";

import { Link } from "react-router-dom";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import OrganizationsAPI from "../../API/organizations.api";
import LookupsAPI from "../../API/lookups.api";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import {
    BOOLEAN_OPTIONS,
    ROLES,
    ORGANIZATION_PREFERENCES,
} from "../../utils/constants";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import UserContext from "../../lib/UserContext";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";
import ProductTags from "../../components/Tags/ProductTags";
import { longDate } from "../../utils/DateHelper";

const Organization = () => {
    const [partners, setPartners] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [filters, setFilters] = useState({});
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("organization-table-filters")
    );
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Organization-itemsPerPage", true) || 10
    );

    const { t } = useTranslation();
    const context = useContext(UserContext);

    const keys = [
        { name: "name" },
        {
            name: "isDemo",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
            element: (row) => {
                if (row.isDemo) {
                    return (
                        <div className="flex items-center justify-center">
                            <div className="tag-ele tag-deployed">
                                {`${row.isDemo}`}
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div className="flex items-center justify-center">
                            <div className="tag-ele tag-completed">
                                {`${row.isDemo}`}
                            </div>
                        </div>
                    );
                }
            },
        },
        { name: "currentActiveUsers" },
        { name: "maxUsersNumber" },
        { name: "type" },
        { name: "endOfLicense" },
        { name: "createdAt" },
        {
            name: "partner",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        {
            name: "storeAccess",
            subKey: "name",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
            element: (row) => (
                <ProductTags
                    center={true}
                    tags={row.storeAccesses}
                    emptyMessage="---"
                    keyName="name"
                />
            ),
        },
    ];

    const headers = [
        { label: t("name"), name: "name" },
        {
            label: t("isDemo"),
            name: "isDemo",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        { label: t("usersCount"), name: "currentActiveUsers" },
        { label: t("maxUsers"), name: "maxUsersNumber" },
        { label: t("type"), name: "type" },
        { label: t("endOfLicense"), name: "endOfLicense" },
        { label: t("createdAt"), name: "createdAt" },
        {
            label: t("partner"),
            name: "partner",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        {
            label: "Store Access",
            name: "storeAccess",
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        { label: t("actions"), skipSort: true },
    ];

    const getAffiliateTypeOptions = () => {
        const options = [
            {
                label: t("enterprise"),
                value: "Enterprise",
                authorized: [ROLES.superAdmin],
            },
            {
                label: t("lab"),
                value: "Lab",
                authorized: [
                    ROLES.superAdmin,
                    ROLES.partnerAdmin,
                    ROLES.support,
                ],
            },
            {
                label: t("farm"),
                value: "Farm",
                authorized: [ROLES.partnerAdmin, ROLES.support],
            },
            {
                label: t("consultant"),
                value: "Consultant",
                authorized: [ROLES.partnerAdmin, ROLES.support],
            },
        ];

        const currentRoles = getRoles();

        return options.filter((option) =>
            isAuthorized(option.authorized, currentRoles)
        );
    };

    const searchFilters = [
        {
            label: t("name"),
            name: "name",
            type: "Input",
            placeholder: "Si-ware",
        },
        {
            label: t("type"),
            name: "type",
            type: "Dropdown",
            options: getAffiliateTypeOptions(),
        },
        {
            label: t("partner"),
            name: "partnerId",
            type: "Dropdown",
            options: partners,
            disabled: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        {
            label: t("isDemo"),
            name: "isDemo",
            type: "Dropdown",
            options: Object.values(BOOLEAN_OPTIONS).map((val) => ({
                label: t(val),
                value: val,
            })),
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        {
            label: t("endOfLicense"),
            name: "endOfLicense",
            type: "Date",
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters(
            "organization-table-filters"
        );
        if (!isEmpty(localStorageFilters)) {
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        }

        const _user = context.getUser();
        if (_user.role === ROLES.superAdmin) {
            fetchPartners();
        }
        setIsRendered(true);
    }, []);

    useEffect(() => {
        if (isRendered) {
            if (sortBy && sortDirection) {
                setCurrentPage(1);
                fetchData(appliedFilters, sortBy, sortDirection);
            } else {
                setCurrentPage(1);
                fetchData(appliedFilters);
            }
        }
    }, [sortBy, sortDirection, isRendered]);

    const applyFilters = (appiedFilters) => {
        fetchData(appiedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const fetchData = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(_filters, ["isDemo"]);
        const _offset = offset < 0 ? 0 : offset; // to ensure you don't get a negative offset

        let result = await OrganizationsAPI.getOrganizations(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );

        setOrganizations(mapOrganizations(result?.data || []));
        setTotalCount(result?.totalCount || 0);
        setIsLoading(false);
    };

    const fetchPartners = async () => {
        try {
            const result = await LookupsAPI.getPartners();
            if (result) {
                setPartners(mapLookupToSelect(result));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const mapOrganizations = (data) => {
        let mapped = [];
        if (data && data.length > 0) {
            mapped = data.map((organization) => {
                return {
                    id: organization.id,
                    name: organization.name,
                    externalId: organization.externalId,
                    currentActiveUsers: organization.currentActiveUsers,
                    maxUsersNumber: organization.maxUsersNumber,
                    type: organization.type,
                    partner: organization.partner?.name || "----",
                    partnerId: organization.partnerId,
                    createdAt: new Date(organization.createdAt),
                    endOfLicense: longDate(new Date(organization.endOfLicense)),
                    image: organization.image,
                    supportedCountryId: organization.supportedCountryId,
                    defaultFirmwareVersion:
                        organization?.defaultFirmwareVersion,
                    storeAccesses: organization.storeAccesses,
                    uploadModelAccess: organization.uploadModelAccess,
                    abbreviation: organization.abbreviation,
                    createBundleAccess: organization.createBundleAccess,
                    defaultCurrencyId: organization.defaultCurrencyId,
                    isDefaultCurrencyEditable:
                        organization.isDefaultCurrencyEditable,
                    createCouponAccess: organization.createCouponAccess,
                    suppliersAccess: organization.suppliersAccess,
                    isDemo: organization.isDemo,
                    preferences: organization.preferences.map((preference) => ({
                        ...preference,
                        entity: ORGANIZATION_PREFERENCES[preference.entity],
                    })),
                };
            });
        }

        return mapped;
    };

    const handleDelete = async (index) => {
        try {
            const result = await OrganizationsAPI.deleteOrganization(
                organizations[index].id
            );
            if (!isEmpty(result)) {
                SuccessAlert(t("deleteAffiliateSuccessMessage"));
                LogCapture.info(
                    `Affiliate deleted with name: ${organizations[index]?.name} & id: ${organizations[index]?.id}`
                );
                let newCurrentPage = currentPage;

                if (organizations.length <= 1 && currentPage > 1) {
                    newCurrentPage = currentPage - 1;
                }

                setCurrentPage(newCurrentPage);
                fetchData(filters, sortBy, sortDirection, newCurrentPage - 1);
                return true;
            } else {
                // ErrorAlert(t("deleteAffiliateFailMessage"));
                LogCapture.error(
                    `Couldn't delete affiliate with name: ${organizations[index]?.name}`
                );
                return false;
            }
        } catch (error) {
            ErrorAlert(error);
            LogCapture.error(
                `Couldn't delete affiliate with name: ${organizations[index]?.name}, error: ${error}`
            );
        }
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            setIsLoading(true);
            // call API to get users with the next pagination
            fetchData(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Organization-itemsPerPage", itemsPerPage, true);

        // call API to update the items per page
        fetchData(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    return (
        <>
            <div id="customer-content" className="customer-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="affiliates">Affiliates</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={searchFilters}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="organization-table"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                        />
                        <Link to="/affiliates/form">
                            <button className="default-orange-outline-btn ml-2">
                                <i className="fas fa-plus mr-2" />
                                <Trans i18nKey="addNewAffiliate">
                                    New Affiliate
                                </Trans>
                            </button>
                        </Link>
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading} text="Loading...">
                <div className="card-table shadow">
                    {organizations?.length > 0 ? (
                        <>
                            <CardTable
                                headers={headers}
                                keys={keys}
                                rows={organizations}
                                handleDelete={handleDelete}
                                purpose={"affiliates"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                actionColumn={true}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={totalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("affiliates")}
                            includeText={!isLoading}
                            path="/affiliates/form"
                        />
                    )}
                </div>
            </LoadingOverlay>
        </>
    );
};

export default Organization;
